import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Upload, Button, Flex, Typography, notification } from "antd";
import { UploadOutlined, SearchOutlined, CloseCircleOutlined } from "@ant-design/icons";

import "./index.scss"; // custom styles for circular overlay
import usePrimaryColor from "helpers/hooks/usePrimaryColor";

const getCroppedImg = (imageSrc, crop) => {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.crossOrigin = "anonymous";
		image.src = imageSrc;
		image.onload = () => {
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");

			canvas.width = crop.width;
			canvas.height = crop.height;

			ctx.beginPath();
			ctx.arc(crop.width / 2, crop.height / 2, crop.width / 2, 0, 2 * Math.PI);
			ctx.closePath();
			ctx.clip();

			ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

			canvas.toBlob((blob) => {
				if (!blob) return reject(new Error("Canvas is empty"));
				const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
				resolve(file);
			}, "image/jpeg");
		};
		image.onerror = () => reject(new Error("Failed to load image"));
	});
};

const AvatarCropper = ({ onCropDone, title, type, setCropStatus, initialImage }) => {
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [imageSrc, setImageSrc] = useState(initialImage);
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
	const [isModalVisible, updateModalVisible] = useState(false);
	const primaryColor = usePrimaryColor();

	const setIsModalVisible = (visible) => {
		updateModalVisible(visible);
		setCropStatus?.(visible);
	};

	const onCropComplete = useCallback((_, croppedAreaPixels) => {
		setCroppedAreaPixels(croppedAreaPixels);
	}, []);

	const handleCrop = async () => {
		const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
		const croppedImageUrl = URL.createObjectURL(croppedImage);
		setImageSrc(croppedImageUrl);
		setIsModalVisible(false);
		onCropDone(croppedImage);
	};
	const handleCancel = () => {
		setIsModalVisible(false);
		setImageSrc(null);
		setCropStatus(false);
	};
	if (imageSrc && !isModalVisible) {
		return (
			<Flex justify="center" align="center" style={{ position: "relative" }}>
				<CloseCircleOutlined
					onClick={handleCancel}
					style={{ position: "absolute", top: 16, right: 100, color: "red" }}
				/>
				<img src={imageSrc} alt="Cropped" style={{ width: 200, height: 200, borderRadius: "50%" }} />
			</Flex>
		);
	}

	return (
		<>
			<Flex className="avatar-uploader" direction="column" justify="center" alignItems="center">
				{!isModalVisible && (
					<Upload.Dragger
						accept="image/*"
						showUploadList={false}
						style={{ flex: 1 }}
						beforeUpload={(file) => {
							const fileSizeLimit = 3 * 1024 * 1024;

							// Validate file type
							if (!file.type.startsWith("image/")) {
								notification.error({ message: "Invalid file type." });
								setImageSrc(null);
								setIsModalVisible(false);
								return Upload.LIST_IGNORE;
							}

							if (file.size > fileSizeLimit) {
								notification.error({ message: "File size exceeds 3 MB limit." });
								setImageSrc(null);
								setIsModalVisible(false);
								return Upload.LIST_IGNORE;
							}

							const reader = new FileReader();
							reader.onload = () => {
								if (reader.result) {
									setImageSrc(reader.result);
								}
							};
							reader.readAsDataURL(file);
							setIsModalVisible(true);
							return false;
						}}
					>
						<Flex align="center" gap={8} vertical>
							<UploadOutlined style={{ fontSize: 40, color: "rgba(0, 0, 0, 0.65)" }} />
							<Typography.Text strong>Click to upload or drag and drop</Typography.Text>
							<Typography.Text>Max. File Size: 3MB</Typography.Text>
							{(type === "video" || title) && <span>{title || "Upload Video"}</span>}
							<Button style={{ backgroundColor: primaryColor }} className="upload-btn" variant="filled">
								<Flex align="center" gap={8}>
									<SearchOutlined />
									Browse File
								</Flex>
							</Button>
						</Flex>
					</Upload.Dragger>
				)}
				{isModalVisible && (
					<Flex vertical>
						<Cropper
							image={imageSrc}
							crop={crop}
							zoom={zoom}
							aspect={1}
							cropShape="round"
							showGrid={false}
							onCropChange={setCrop}
							onCropComplete={onCropComplete}
							onZoomChange={setZoom}
							style={{
								cropAreaStyle: {
									border: `2px solid ${primaryColor}`,
								},
								containerStyle: {
									height: 250,
									boxSizing: "border-box",
								},
							}}
						/>
					</Flex>
				)}
			</Flex>
			{isModalVisible && (
				<Flex className="sb-confirm-form__actions" justify="space-between" style={{ marginTop: 16, marginBottom: 16 }}>
					<Button
						onClick={() => {
							setIsModalVisible(false);
							setImageSrc(null);
						}}
					>
						Cancel
					</Button>
					<Button type="primary" variant="filled" onClick={handleCrop}>
						Upload
					</Button>
				</Flex>
			)}
		</>
	);
};

export default AvatarCropper;

